import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { roomAPI, waitingListAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Search, SlidersHorizontal, Bell, Mail, Phone, User, CheckCircle } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import OptimizedImage from '../components/OptimizedImage';

const BrowseRooms = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 1500]);
  const [bathType, setBathType] = useState('all');
  const [location, setLocation] = useState('all');
  const [sortBy, setSortBy] = useState('price-low');
  
  // Waiting list form state
  const [waitingListForm, setWaitingListForm] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [submittingWaitingList, setSubmittingWaitingList] = useState(false);
  const [waitingListSubmitted, setWaitingListSubmitted] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleWaitingListSubmit = async (e) => {
    e.preventDefault();
    
    if (!waitingListForm.name || !waitingListForm.email || !waitingListForm.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in your name, email, and phone number",
        variant: "destructive"
      });
      return;
    }
    
    setSubmittingWaitingList(true);
    try {
      await waitingListAPI.add(waitingListForm);
      setWaitingListSubmitted(true);
      toast({
        title: "You're on the list! ✅",
        description: "We'll contact you when a room becomes available.",
        variant: "success"
      });
    } catch (error) {
      console.error('Error joining waiting list:', error);
      const errorMessage = error.response?.data?.detail || "Failed to join waiting list. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubmittingWaitingList(false);
    }
  };

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await roomAPI.getRooms({ status: 'Available' });
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort rooms
  const filteredRooms = rooms
    .filter(room => {
      const matchesSearch = room.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           room.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           room.property_city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           room.property_state?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           room.property_zip?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPrice = room.rent >= priceRange[0] && room.rent <= priceRange[1];
      const matchesBath = bathType === 'all' || room.bath_type === bathType;
      const matchesLocation = location === 'all' || (room.property_city || '').toLowerCase() === location.toLowerCase();
      return matchesSearch && matchesPrice && matchesBath && matchesLocation;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.rent - b.rent;
        case 'price-high':
          return b.rent - a.rent;
        default:
          return 0;
      }
    });

  // Unique list of city / state combos derived from currently loaded rooms
  const availableLocations = useMemo(() => {
    const seen = new Map();
    rooms.forEach(r => {
      const city = (r.property_city || '').trim();
      if (!city) return;
      const state = (r.property_state || '').trim();
      const label = state ? `${city}, ${state}` : city;
      if (!seen.has(city.toLowerCase())) {
        seen.set(city.toLowerCase(), { value: city, label });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rooms]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Available Rooms in Atlanta
          </h1>
          <p className="text-gray-600 mb-6">
            {loading ? 'Loading...' : `${filteredRooms.length} rooms available`}
          </p>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by city, address, or neighborhood..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>

            {/* Mobile Filters */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="md:hidden">
                  <SlidersHorizontal className="h-5 w-5 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  <div>
                    <Label>Price Range: ${priceRange[0]} - ${priceRange[1]}</Label>
                    <Slider
                      min={0}
                      max={1500}
                      step={50}
                      value={priceRange}
                      onValueChange={setPriceRange}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Bathroom Type</Label>
                    <Select value={bathType} onValueChange={setBathType}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Private">Private</SelectItem>
                        <SelectItem value="Shared">Shared</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger className="mt-2" data-testid="filter-location-mobile">
                        <SelectValue placeholder="All Locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {availableLocations.map(loc => (
                          <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-4">Filters</h3>
                
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-medium">Price Range</Label>
                    <div className="text-sm text-gray-600 mb-2">
                      ${priceRange[0]} - ${priceRange[1]}
                    </div>
                    <Slider
                      min={0}
                      max={1500}
                      step={50}
                      value={priceRange}
                      onValueChange={setPriceRange}
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Bathroom Type</Label>
                    <Select value={bathType} onValueChange={setBathType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="Private">Private</SelectItem>
                        <SelectItem value="Shared">Shared</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Location</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger data-testid="filter-location-desktop">
                        <SelectValue placeholder="All Locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Locations</SelectItem>
                        {availableLocations.map(loc => (
                          <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setPriceRange([0, 1500]);
                      setBathType('all');
                      setLocation('all');
                      setSearchTerm('');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Rooms Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading rooms...</p>
              </div>
            ) : rooms.length === 0 ? (
              /* No rooms available at all - Show waiting list form */
              <Card className="border-2 border-dashed border-orange-300">
                <CardContent className="py-8">
                  {waitingListSubmitted ? (
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">You're on the List!</h3>
                      <p className="text-gray-600 max-w-md mx-auto">
                        Thank you for your interest! We'll contact you as soon as a room becomes available.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Bell className="h-8 w-8 text-orange-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Rooms Available Right Now</h3>
                        <p className="text-gray-600 max-w-md mx-auto">
                          All our rooms are currently occupied. Join our waiting list and we'll notify you as soon as a room becomes available!
                        </p>
                      </div>
                      
                      <form onSubmit={handleWaitingListSubmit} className="max-w-md mx-auto space-y-4">
                        <div>
                          <Label htmlFor="wl-name" className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Full Name *
                          </Label>
                          <Input
                            id="wl-name"
                            type="text"
                            placeholder="John Doe"
                            value={waitingListForm.name}
                            onChange={(e) => setWaitingListForm({...waitingListForm, name: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="wl-email" className="flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Email Address *
                          </Label>
                          <Input
                            id="wl-email"
                            type="email"
                            placeholder="john@example.com"
                            value={waitingListForm.email}
                            onChange={(e) => setWaitingListForm({...waitingListForm, email: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="wl-phone" className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            Phone Number *
                          </Label>
                          <Input
                            id="wl-phone"
                            type="tel"
                            placeholder="(555) 123-4567"
                            value={waitingListForm.phone}
                            onChange={(e) => setWaitingListForm({...waitingListForm, phone: e.target.value})}
                            required
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="wl-notes">Additional Notes (Optional)</Label>
                          <Textarea
                            id="wl-notes"
                            placeholder="Tell us about your preferred move-in date, budget, or any specific requirements..."
                            value={waitingListForm.notes}
                            onChange={(e) => setWaitingListForm({...waitingListForm, notes: e.target.value})}
                            rows={3}
                            className="mt-1"
                          />
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full bg-orange-600 hover:bg-orange-700"
                          disabled={submittingWaitingList}
                        >
                          {submittingWaitingList ? 'Joining...' : 'Join Waiting List'}
                        </Button>
                        
                        <p className="text-xs text-gray-500 text-center">
                          We'll only contact you about room availability. Your information is kept private.
                        </p>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : filteredRooms.length === 0 ? (
              /* Rooms exist but filters don't match */
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-600">No rooms found matching your criteria.</p>
                  <Button
                    variant="link"
                    onClick={() => {
                      setPriceRange([0, 1500]);
                      setBathType('all');
                      setSearchTerm('');
                    }}
                  >
                    Clear all filters
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredRooms.map((room) => (
                  <Card
                    key={room.id || room._id}
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/rooms/${room.id || room._id}`)}
                  >
                    <div className="relative h-56">
                      <OptimizedImage
                        src={room.images && room.images.length > 0 
                          ? (room.images[0].startsWith('http') ? room.images[0] : `${process.env.REACT_APP_BACKEND_URL}${room.images[0]}`)
                          : null}
                        alt={room.title}
                        className="w-full h-full"
                      />
                      {room.status === 'Available Soon' ? (
                        <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Available Soon
                        </div>
                      ) : (
                        <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          Available Now
                        </div>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-xl font-semibold mb-2">{room.title}</h3>
                      {room.status === 'Available Soon' && room.available_date && (
                        <div className="bg-orange-50 border border-orange-200 rounded-md px-3 py-2 mb-3">
                          <p className="text-sm text-orange-700 font-medium">
                            📅 Available from: {new Date(room.available_date).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                      <p className="text-gray-600 text-sm mb-3 flex items-start">
                        <MapPin className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                        <span>
                          {room.address || room.description?.substring(0, 50) + '...'}
                          {(room.property_city || room.property_state || room.property_zip) && (
                            <span className="block text-gray-500">
                              {[room.property_city, room.property_state].filter(Boolean).join(', ')}
                              {(room.property_city || room.property_state) && room.property_zip ? ' ' : ''}
                              {room.property_zip || ''}
                            </span>
                          )}
                        </span>
                      </p>
                      <div className="flex items-center space-x-2 mb-3 text-sm text-gray-500">
                        <span>{room.bath_type} Bath</span>
                        <span>•</span>
                        <span>{room.square_feet} sq ft</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {room.amenities?.slice(0, 3).map((amenity, idx) => (
                          <span
                            key={idx}
                            className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                      <div className="flex justify-between items-center pt-4 border-t">
                        <div>
                          <p className="text-sm text-gray-500">Monthly Rent</p>
                          <p className="text-2xl font-bold text-gray-900">
                            ${room.rent}
                            <span className="text-sm font-normal text-gray-600">/mo</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Deposit: <span className="font-semibold text-gray-700">${room.deposit || room.rent}</span>
                          </p>
                        </div>
                        <Button 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/rooms/${room.id || room._id}`);
                          }}
                        >
                          Details & Apply
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowseRooms;